import { CURRENCY } from './variables';
import CONTENT_STRINGS from './contentStrings';
import { formattedMoney } from './functions';
import {
    AMOUNT,
    CONTINGENT,
    GROUP,
    LABEL,
    NON_CONTINGENT,
    NOT_AVAILABLE,
    NOT_DUE,
    PAID,
    TAX,
    TOTAL,
    TYPE,
    UNPAID_AMOUNT,
    WRITEBACK,
} from './constants';



// null/undefined -> "N/A" (no config to derive from); a real 0 still formats as a real amount, per the
// Financials tab's fallback rules (Premium/IPT are N/A, not 0, when nothing resolved).
export const formatMoneyOrNA = (value) => {
    const result = value === null || value === undefined ? NOT_AVAILABLE : formattedMoney(value, CURRENCY);
    return result;
}

// One row per agent, backend already backfills a 0 row for any agent listed on the policy who has no
// commission configured for this premium type, so this just formats what it's given.
export const buildAgentCommissionRows = (agentCommissions) => {
    const agentCommissionRows = (agentCommissions || []).map((c) => ({
        orgId: c.orgId,
        name: c.name,
        commission: formatMoneyOrNA(c.commission),
    }));
    return agentCommissionRows;
}

// Shapes one premium type's block (Contingent or Non-Contingent) for display. `premiumTypeDto` is never
// null here - buildFinancialsColumnSet filters those fields out first; the ?./?? below are just defensive.
export const buildPremiumTypeSummary = (premiumTypeDto) => {
    const premium = formatMoneyOrNA(premiumTypeDto?.premium ?? null);
    const ipt = formatMoneyOrNA(premiumTypeDto?.ipt ?? null);
    const agentCommissionRows = buildAgentCommissionRows(premiumTypeDto?.agentCommissions);
    return {
        premium,
        ipt,
        agentCommissionRows,
    };
};

// Display order per AC1: Contingent, Writeback, Non-Contingent. `field` is the DTO property backing the
// column; writeback is null on every non-percentage policy, so the column simply doesn't appear.
const COLUMN_DEFS = [
    { key: CONTINGENT, header: CONTENT_STRINGS.CONTINGENT, field: 'contingent' },
    { key: WRITEBACK, header: CONTENT_STRINGS.WRITEBACK, field: 'writeback' },
    { key: NON_CONTINGENT, header: CONTENT_STRINGS.NON_CONTINGENT, field: 'nonContingent' },
];

// The single place "which columns does this policy have" is decided - every helper below reads this.
// `raw` keeps the un-formatted premiumTypeDto alongside `summary` (already formatted for display) - the
// Totals row (AC6) needs the raw numeric premium/ipt to add them before formatting, not the display strings.
export const buildFinancialsColumnSet = (financials) => COLUMN_DEFS
    .filter((def) => !!financials[def.field])
    .map((def) => ({ ...def, raw: financials[def.field], summary: buildPremiumTypeSummary(financials[def.field]) }));

// Object.fromEntries appears nowhere in this codebase, so cells are reduced in rather than mapped.
const rowForColumns = (label, columnSet, pickValue) => columnSet.reduce((row, column) => {
    row[column.key] = pickValue(column);
    return row;
}, { [LABEL]: label });

// WI 100878 AC2: a leading grouping column ("Premium" / "Agent Commission"), then the row-label column
// (Net Premium/IPT/Total-Gross Premium, then one row per commission agent), then the premium-type columns.
// GROUP's header is a single space, not '', because the mobile accordion (tableAccordion.js) keys each
// field by column.header - two columns both keyed by '' produce duplicate React keys.
export const financialsTableColumns = (columnSet) => [
    { propertyName: GROUP, header: ' ' },
    { propertyName: LABEL, header: '' },
    ...columnSet.map((column) => ({ propertyName: column.key, header: column.header })),
];

// One table row per agent org ID present on any column's commission list.
export const buildAgentCommissionTableRows = (columnSet) => {
    const orgIds = [...new Set(columnSet.flatMap((column) => column.summary.agentCommissionRows.map((row) => row.orgId)))];

    const commissionAgents = orgIds.map((orgId) => {
        const entries = columnSet.map((column) => ({
            key: column.key,
            row: column.summary.agentCommissionRows.find((row) => row.orgId === orgId),
        }));
        // Name is null when the Lawsys firm lookup misses for this orgId (ApplyAgentNames in
        // ApplicationService.cs uses GetValueOrDefault) - fall back to the orgId so the row is still
        // identifiable instead of showing a blank label.
        const name = entries.map((entry) => entry.row?.name).find((agentName) => !!agentName) || String(orgId);
        return entries.reduce((row, entry) => {
            row[entry.key] = entry.row ? entry.row.commission : NOT_AVAILABLE;
            return row;
        }, { [LABEL]: name });
    });
    return commissionAgents;
};

// AC6: Totals = Premium + IPT for that column. Falls back to N/A (via formatMoneyOrNA) rather than
// treating a missing premium/ipt as 0 - a partial figure would misrepresent the total, same reasoning
// formatMoneyOrNA already applies to Premium/IPT themselves.
const buildTotalsRow = (columnSet) => rowForColumns(CONTENT_STRINGS.TOTALS_GROSS_PREMIUM, columnSet, (column) => {
    const { premium, ipt } = column.raw ?? {};
    const total = premium != null && ipt != null ? premium + ipt : null;
    return formatMoneyOrNA(total);
});

// WI 100878 AC1: Net Premium, IPT, Total/Gross Premium (the Premium group), then one row per commission
// agent (the Agent Commission group). Each row carries its grouping-column heading in GROUP - shown only
// on the group's first row (see the formatter in financialsTab.js), blank on the rest.
export const buildFinancialsTableData = (columnSet) => {
    const agentCommissionRows = buildAgentCommissionTableRows(columnSet);
    const premiumRows = [
        rowForColumns(CONTENT_STRINGS.NET_PREMIUM, columnSet, (column) => column.summary.premium),
        rowForColumns(TAX, columnSet, (column) => column.summary.ipt),
        buildTotalsRow(columnSet),
    ];
    const withGroupHeading = (rows, heading) => rows.map((row, index) => ({
        ...row,
        [GROUP]: index === 0 ? heading : '',
    }));
    return [
        ...withGroupHeading(premiumRows, CONTENT_STRINGS.PREMIUM),
        ...withGroupHeading(agentCommissionRows, CONTENT_STRINGS.AGENT_COMMISSION),
    ];
};

// Financial Position section: every numeric field on a row is a real (non-nullable) decimal from the
// backend - a genuine 0 formats as a real amount, never N/A, unlike Current Premium Information's
// formatMoneyOrNA fields (which use null to mean "nothing configured").
export const buildOwedRows = (rows) => (rows || []).map((r) => ({
    [TYPE]: r.type,
    [TOTAL]: formattedMoney(r.totalSalesOrExpenses, CURRENCY),
    [NOT_DUE]: formattedMoney(r.notDue, CURRENCY),
    [AMOUNT]: formattedMoney(r.amount, CURRENCY),
    [PAID]: formattedMoney(r.paid, CURRENCY),
    [UNPAID_AMOUNT]: formattedMoney(r.unpaidAmount, CURRENCY),
}));

// Shared by both Financial Position tables - only the first two headers differ ("Sales Type"/"Total
// Sales" vs "Expenses Type"/"Total Expenses"); Due + Paid/Paid/Unpaid Amount are identical on both
// tables. AC1: the AMOUNT column is relabelled "Due + Paid" (via the new DUE_AND_PAID constant, not the
// shared CONTENT_STRINGS.AMOUNT used elsewhere in the app) rather than renamed, and AC2 adds Paid
// immediately after it.
export const financialPositionColumns = (typeHeader, totalHeader) => [
    { propertyName: TYPE, header: typeHeader },
    { propertyName: TOTAL, header: totalHeader},
    { propertyName: NOT_DUE, header: CONTENT_STRINGS.NOT_DUE},
    { propertyName: AMOUNT, header: CONTENT_STRINGS.DUE_AND_PAID},
    { propertyName: PAID, header: CONTENT_STRINGS.FINANCIAL_POSITION_PAID},
    { propertyName: UNPAID_AMOUNT, header: CONTENT_STRINGS.UNPAID_AMOUNT},
];
